import { z } from "zod";

import { CatalogKitConfigSchema, QualityIssueSchema } from "@/types";

/**
 * Runtime-validated schemas for the v0.2 IndexedDB entities: profiles,
 * snapshots, quotes, audit-log entries, saved searches, and pending uploads.
 *
 * Zod is the source of truth; TypeScript interfaces are derived via `z.infer`.
 * All ids are UUIDv4 strings and all timestamps are ISO-8601 strings, matching
 * the project data model and `shared/schemas/*.schema.json`. Timestamps are
 * validated as plain strings (we always write `new Date().toISOString()`), to
 * keep reads resilient across time zones and future format tweaks.
 */

const uuid = z.string().uuid();
const isoTimestamp = z.string().min(1);

// --------------------------------------------------------------------------- //
// CatalogProfile
// --------------------------------------------------------------------------- //
export const AutoMatchMethodSchema = z.enum([
  "headers",
  "headers_and_categories",
  "content_similarity",
  "manual",
]);
export type AutoMatchMethod = z.infer<typeof AutoMatchMethodSchema>;

export const CatalogProfileSchema = z.object({
  id: uuid,
  label: z.string().min(1),
  source_system: z.string().default("connectwise"),
  created_at: isoTimestamp,
  last_used_at: isoTimestamp,
  default_config: CatalogKitConfigSchema.optional(),
  /** `null` = unlimited retention (the default); a positive number keeps the last N days. */
  retention_days: z.number().int().positive().nullable().default(null),
  auto_match_method: AutoMatchMethodSchema.default("headers"),
  notes: z.string().optional(),
  archived: z.boolean().default(false),
  /** Navigate to the diff automatically after an upload (default on). */
  auto_diff_on_upload: z.boolean().default(true),
  /** Reverse index of the profile's snapshot ids (newest last). */
  snapshot_ids: z.array(uuid).default([]),
});
export type CatalogProfile = z.infer<typeof CatalogProfileSchema>;

// --------------------------------------------------------------------------- //
// CatalogSnapshot
// --------------------------------------------------------------------------- //
export const CatalogSnapshotSchema = z.object({
  id: uuid,
  profile_id: uuid,
  label: z.string().optional(),
  source_filename: z.string(),
  uploaded_at: isoTimestamp,
  row_count: z.number().int().nonnegative(),
  /** gzip-compressed JSON of the `NormalizedRow[]` (fflate). Stored as bytes. */
  rows_compressed: z.instanceof(Uint8Array),
  quality_issues: z.array(QualityIssueSchema).default([]),
  config_snapshot: CatalogKitConfigSchema,
  quality_score: z.number().min(0).max(100),
  avg_margin_percent: z.number(),
  total_price_value: z.number(),
  categories: z.record(z.string(), z.number()).default({}),
  notes: z.string().optional(),
});
export type CatalogSnapshot = z.infer<typeof CatalogSnapshotSchema>;

/** Snapshot metadata without the heavy compressed row payload (for lists). */
export type SnapshotMeta = Omit<CatalogSnapshot, "rows_compressed">;

// --------------------------------------------------------------------------- //
// QuoteRecord (v0.2 stub entity; API import lands in v0.3)
// --------------------------------------------------------------------------- //
export const QuoteLineItemSchema = z.object({
  product_id: z.string(),
  description: z.string(),
  qty: z.number(),
  /** Unit price frozen at quote time (decimal string for exactness). */
  unit_price: z.string(),
  /** Unit cost frozen at quote time (decimal string for exactness). */
  unit_cost: z.string(),
});
export type QuoteLineItem = z.infer<typeof QuoteLineItemSchema>;

export const QuoteStatusSchema = z.enum(["draft", "sent", "accepted", "declined", "expired"]);
export type QuoteStatus = z.infer<typeof QuoteStatusSchema>;

export const QuoteRecordSchema = z.object({
  id: uuid,
  profile_id: uuid,
  quote_id_external: z.string().optional(),
  customer_name: z.string(),
  status: QuoteStatusSchema,
  expected_close_date: z.string().optional(),
  sales_owner: z.string().optional(),
  notes: z.string().optional(),
  line_items: z.array(QuoteLineItemSchema).default([]),
  created_at: isoTimestamp,
  updated_at: isoTimestamp,
  imported_from: z.enum(["manual", "csv", "api"]),
});
export type QuoteRecord = z.infer<typeof QuoteRecordSchema>;

// --------------------------------------------------------------------------- //
// AuditLogEntry
// --------------------------------------------------------------------------- //
export const AuditCategorySchema = z.enum([
  "upload",
  "export",
  "bulk_edit",
  "profile_change",
  "config_change",
  "delete",
  "restore",
  "compare",
  "quality_action",
  "connection_change",
]);
export type AuditCategory = z.infer<typeof AuditCategorySchema>;

export const AuditLogEntrySchema = z.object({
  id: uuid,
  /** `undefined` for global (non-profile-scoped) events. */
  profile_id: uuid.optional(),
  timestamp: isoTimestamp,
  category: AuditCategorySchema,
  action: z.string(),
  actor: z.literal("user"),
  details: z.record(z.string(), z.unknown()).default({}),
  before_state: z.unknown().optional(),
  after_state: z.unknown().optional(),
});
export type AuditLogEntry = z.infer<typeof AuditLogEntrySchema>;

// --------------------------------------------------------------------------- //
// SavedSearch
// --------------------------------------------------------------------------- //
/**
 * Serialized filter state for a saved search. Kept intentionally permissive at
 * v0.2 (the DataTable filter surface is still evolving); tightened when the
 * Saved Searches feature ships its CRUD layer.
 */
export const FilterStateSchema = z.record(z.string(), z.unknown());
export type FilterState = z.infer<typeof FilterStateSchema>;

export const SavedSearchSchema = z.object({
  id: uuid,
  profile_id: uuid.optional(),
  label: z.string(),
  filters: FilterStateSchema.default({}),
  is_pinned: z.boolean().default(false),
  created_at: isoTimestamp,
});
export type SavedSearch = z.infer<typeof SavedSearchSchema>;

// --------------------------------------------------------------------------- //
// PendingUpload (not in the formal data model; see design notes)
// --------------------------------------------------------------------------- //
/**
 * A parsed-but-unassigned upload held while the user picks/confirms a profile
 * or resolves a storage-quota prompt. Rows are stored compressed, like a
 * snapshot, so a large upload does not sit in memory.
 */
export const PendingUploadSchema = z.object({
  id: uuid,
  uploaded_at: isoTimestamp,
  source_filename: z.string(),
  headers: z.array(z.string()).default([]),
  row_count: z.number().int().nonnegative(),
  rows_compressed: z.instanceof(Uint8Array),
  suggested_profile_id: uuid.optional(),
});
export type PendingUpload = z.infer<typeof PendingUploadSchema>;


// --------------------------------------------------------------------------- //
// CWConnection (v0.3 — ConnectWise connection profiles)
// --------------------------------------------------------------------------- //
export const CWAuthMethodSchema = z.enum(["public_private_key", "api_member", "client_id_only"]);
export type CWAuthMethod = z.infer<typeof CWAuthMethodSchema>;

export const CWCredentialModeSchema = z.enum(["encrypted", "session_only"]);
export type CWCredentialMode = z.infer<typeof CWCredentialModeSchema>;

/**
 * A ConnectWise Manage connection profile. Credential fields (`public_key`,
 * `private_key`, `proxy_session_token`) hold **vault-encrypted bundles**
 * (serialized JSON) when `credential_mode` is `encrypted`; in `session_only`
 * mode they are absent and live only in process memory (`db/connections.ts`).
 * Plaintext credentials are never persisted.
 */
export const CWConnectionSchema = z.object({
  id: uuid,
  label: z.string().min(1),
  /** e.g. "na.myconnectwise.net" — hostname only, no scheme. */
  site: z.string().min(1),
  company_id: z.string().min(1),
  auth_method: CWAuthMethodSchema,
  credential_mode: CWCredentialModeSchema,
  /** Required for public/private key auth. */
  member_id: z.string().optional(),
  /** Vault-encrypted bundle JSON (never plaintext). */
  public_key: z.string().optional(),
  /** Vault-encrypted bundle JSON (never plaintext). */
  private_key: z.string().optional(),
  client_id: z.string().min(1),
  proxy_url: z.string().min(1).default("http://127.0.0.1:24601"),
  /** Vault-encrypted bundle JSON (never plaintext). */
  proxy_session_token: z.string().optional(),
  auto_lock_minutes: z.number().int().positive().default(240),
  read_only: z.boolean().default(false),
  created_at: isoTimestamp,
  last_connected_at: isoTimestamp.optional(),
  archived: z.boolean().default(false),
});
export type CWConnection = z.infer<typeof CWConnectionSchema>;

// Re-export the shared row/issue types so DB modules have a single import site.
export type { NormalizedRow, QualityIssue } from "@/types";
